import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getServices, getServiceHealth } from '@/api/api';
import { Server, Cloud, AlertTriangle, CheckCircle, Activity } from 'lucide-react';

const ServicesOverview = () => {
  const [services, setServices] = useState([]);
  const [serviceHealth, setServiceHealth] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const servicesData = await getServices();
      setServices(servicesData);

      const healthPromises = servicesData.map((service) =>
        getServiceHealth(service.name).then((health) => ({
          name: service.name,
          health,
        }))
      );

      const healthResults = await Promise.all(healthPromises);
      const healthMap = {};
      healthResults.forEach(({ name, health }) => {
        healthMap[name] = health;
      });
      setServiceHealth(healthMap);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching services:', error);
      setLoading(false);
    }
  };

  const getCloudProviderColor = (provider) => {
    const colors = {
      AWS: 'bg-orange-100 text-orange-800 border-orange-200',
      GCP: 'bg-blue-100 text-blue-800 border-blue-200',
      IBM: 'bg-purple-100 text-purple-800 border-purple-200',
      AZURE: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    };
    return colors[provider] || 'bg-gray-100 text-gray-800';
  };

  const getHealthStatusColor = (status) => {
    const colors = {
      healthy: 'bg-green-100 text-green-800',
      degraded: 'bg-yellow-100 text-yellow-800',
      critical: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getServiceTypeIcon = (type) => {
    const icons = {
      API_GATEWAY: '🌐',
      GRAPHQL: '📊',
      MICROSERVICE: '⚙️',
      REST: '🔗',
      DATABASE: '🗄️',
      CACHE: '⚡',
      MESSAGE_QUEUE: '📬',
    };
    return icons[type] || '📦';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="services-loading">
        <Activity className="w-12 h-12 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="services-overview">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Services Overview</h2>
        <p className="text-gray-600 mt-1">Monitor all microservices across multi-cloud environments</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((service) => {
          const health = serviceHealth[service.name];
          const healthStatus = health?.health_status || 'unknown';

          return (
            <Card
              key={service.id}
              className="hover:shadow-lg transition-shadow"
              data-testid={`service-card-${service.name}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{getServiceTypeIcon(service.service_type)}</span>
                    <div>
                      <CardTitle className="text-lg">{service.name}</CardTitle>
                      <p className="text-xs text-gray-500 mt-1">{service.service_type.replace('_', ' ')}</p>
                    </div>
                  </div>
                  {service.is_active && (
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  )}
                </div>
              </CardHeader>

              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Cloud Provider</span>
                  <Badge variant="outline" className={getCloudProviderColor(service.cloud_provider)}>
                    <Cloud className="w-3 h-3 mr-1" />
                    {service.cloud_provider}
                  </Badge>
                </div>

                {health && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Health Status</span>
                    <Badge className={getHealthStatusColor(healthStatus)}>
                      {healthStatus === 'healthy' ? (
                        <CheckCircle className="w-3 h-3 mr-1" />
                      ) : (
                        <AlertTriangle className="w-3 h-3 mr-1" />
                      )}
                      {healthStatus}
                    </Badge>
                  </div>
                )}

                {health && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Active Anomalies</span>
                    <Badge
                      variant={health.active_anomalies > 0 ? 'destructive' : 'default'}
                      className={health.active_anomalies === 0 ? 'bg-green-100 text-green-800' : ''}
                    >
                      {health.active_anomalies}
                    </Badge>
                  </div>
                )}

                {service.dependencies.length > 0 && (
                  <div>
                    <span className="text-sm text-gray-600 mb-2 block">Dependencies</span>
                    <div className="flex flex-wrap gap-1">
                      {service.dependencies.slice(0, 3).map((dep, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {dep}
                        </Badge>
                      ))}
                      {service.dependencies.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{service.dependencies.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default ServicesOverview;
